import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';

import type {
  ChatCardItem,
  ChatMessage,
  ConversationalFormOption,
  ExtractedFact,
} from '@karehero/models';
import { DocumentTagID, TRIAGE_QUESTION_ID } from '@karehero/models';
import { formatFullName } from '@karehero/helpers/name';
import { words } from '@karehero/helpers/string';

import { useDispatch, useSelector } from 'store';
import { newToast } from 'store/slices/uiSlice';
import { invalidateNotificationTags } from 'store/api/notification';
import { useUpdateAccountConsentMutation } from 'store/api/account';
import { useGetCareProfileAnswersQuery } from 'store/api/careProfile';
import { useHandleTriageResponsesMutation } from 'store/api/triage';
import { getCurrentCareCircle } from 'store/slices/careCircleSlice';
import { clearIsNewKindeAccount } from 'store/slices/iamSlice';
import {
  withConsentStyle,
  type ConsentStyle,
  type OnboardingV4Beat,
  type OnboardingV4FlowID,
  type Who,
} from 'config/onboardingV4/flows';
import { triageQuestions, type TriageQuestion } from 'config/triageQuestions';
import * as copy from 'config/onboardingV4/copy';
import { NEED_ITEMS } from 'config/onboardingV4/needs';
import { AGE_BRACKET_ID } from 'config/benefitsCalculatorV2Steps';
import {
  BEAT_STEPS,
  CHANGE_AGE,
  CONSENT_AGREE,
  HEARD_NOT_QUITE,
  YES,
} from 'config/onboardingV4/beatSteps';
import {
  optionLabels,
  predictedAnswers,
  type Knowledge,
} from 'utils/triageKnowledge';
import {
  ageChangeAsked,
  ageHeard,
  ageSaid,
  ageTapped,
  answeredInOwnWords,
  correctingKey,
  currentBeat,
  gathered,
  heardAgain,
  heardKey,
  helpWithKey,
  isHelpWithAsked,
  isHelpWithAskedIn,
  menuPicked,
  needSkipped,
  readingJoined,
  readingStarted,
  sentRead,
  summaryEdited,
  under18Key,
} from 'utils/onboardingV4Progress';
import {
  openMenuID,
  readingItems,
  shownMessages,
} from 'utils/onboardingV4Messages';
import {
  askFor,
  choiceStyleFor,
  interjectionFor,
  questionFor,
  sectionFor,
  stepFor,
  type OnboardingV4Step,
} from 'utils/onboardingV4Steps';
import { answersByQuestion } from 'utils/careProfileAnswers';
import {
  answersFrom,
  card,
  includesVoiceNote,
  isMessageRead,
  lastCardID,
  message,
  say,
  textMessage,
} from 'utils/chatMessages';
import {
  nextAction,
  QUESTIONS_INTRO,
  type Transition,
} from 'utils/onboardingV4Director';
import { namePartsFromFacts } from 'utils/fullNameFromFacts';
import {
  isAnsweredFrom,
  isCareForSelf,
  isPhoneKnown,
} from 'utils/extractedFacts';
import { ukPhoneProblem } from 'utils/ukPhoneNumber';
import { firstNameOf, type Person } from 'utils/person';
import {
  useExtractedFacts,
  useExtractedFactsFetchedAt,
  useIsExtractedFactsLoaded,
} from './useExtractedFacts';
import { useHasChangedSince } from './useHasChangedSince';
import { useFeatureFlag } from './useFeatureFlags';
import { useIntakeSummary } from './useIntakeSummary';
import { useHasElapsed } from './useHasElapsed';
import { useChatSender } from './useChatSender';
import { useNeedChecklist } from './useNeedChecklist';
import { useOnboardingHaptics } from './useOnboardingHaptics';
import { useOnboardingV4Confirm } from './useOnboardingV4Confirm';
import { useOnboardingV4Session } from './useOnboardingV4Session';
import { useOnboardingTypingWait } from './useOnboardingTypingWait';
import { usePinnedPrompts } from './usePinnedPrompts';
import { useSendHold } from './useSendHold';
import { useChatSystemSender } from './useChatSystemSender';
import { LIVE_TRANSCRIBE_FLAG } from './useVoiceTranscript';
import { TrackingEvent, useTracking } from './useTracking';

const AI_CONSENT_FORM_ID = 'ai-data-processing-v1';
/** Where what they say is from, as it is sent to be read. */
const CAPTURE_SOURCE = 'onboarding';

/** The beat before Keni's next lines land. */
const THINK_DELAY_MS = 900;

/** How long the summary is waited on once the conversation reaches it. */
const SUMMARY_WAIT_MS = 30_000;

/** Fewer words than this is too little to tell us about their situation. */
const MIN_TALK_WORDS = 3;

/** How the triage question on who is cared for is answered. */
type CareRecipientTypeAnswer = 'someone-else' | 'self' | 'exploring';

const TRIAGE_CARE_RECIPIENT_TYPE: Record<Who, CareRecipientTypeAnswer> = {
  'someone-else': 'someone-else',
  myself: 'self',
  exploring: 'exploring',
};

/** Who the care is for, as what they said reads it; unknown until it is clear. */
const whoSaid = (facts: readonly ExtractedFact[]): Who | undefined => {
  const isForSelf = isCareForSelf(facts);
  if (isForSelf === undefined) return undefined;
  return isForSelf ? 'myself' : 'someone-else';
};

export interface UseOnboardingV4Options {
  /** The flow a new session starts, or undefined while the flag loads; a begun session keeps its own. */
  flowID?: OnboardingV4FlowID;
  /** Where a new session asks consent to AI; a begun session keeps its own. */
  consentStyle?: ConsentStyle;
  /** Whether they are recording or typing, so the prompts can come back. */
  isComposing?: boolean;
  /** Called once onboarding is done, with where they chose to start. */
  onFinished: (start?: string) => void;
  /** Called once a decline of AI consent is saved. */
  onDeclined: () => void;
  /** Zero lands every message straight away. */
  thinkDelayMs?: number;
  /** What is typed or recorded but not yet sent, so the checklists tick as it is said. */
  liveText?: string;
  /** Whether the microphone was refused, so Keni says how to let it in. */
  isMicBlocked?: boolean;
}

/** useOnboardingV4 runs one conversation as its flow gives, asking only what the talk left open. */
export const useOnboardingV4 = ({
  flowID,
  consentStyle,
  isComposing = false,
  onFinished,
  onDeclined,
  thinkDelayMs = THINK_DELAY_MS,
  liveText = '',
  isMicBlocked = false,
}: UseOnboardingV4Options) => {
  const dispatch = useDispatch();
  const { track } = useTracking();
  const {
    careCircleId,
    sessionId,
    flow: sessionFlow,
    apiMessages,
    messages,
    processed,
    progress,
    saveProgress,
    finishBeat,
    markAsked,
    setStage,
    completeSession,
    persistMessage,
    updateMessage,
    sendMessage,
    resendMessage,
    sentCount,
  } = useOnboardingV4Session({ flowID, consentStyle });
  const flow = useMemo(
    () => withConsentStyle(sessionFlow, progress?.consentStyle ?? 'screen'),
    [progress?.consentStyle, sessionFlow],
  );

  const [updateAccountConsent] = useUpdateAccountConsentMutation();
  const [handleTriageResponses] = useHandleTriageResponsesMutation();

  const isVoiceFlagOn = !!useFeatureFlag('audio-recording-enabled');
  const isFileFlagOn = !!useFeatureFlag('onboarding-file-upload');
  const isCoverageFlagOn = !!useFeatureFlag(LIVE_TRANSCRIBE_FLAG);

  const canRead = !!progress?.isAiConsentGiven && !!careCircleId;
  const facts = useExtractedFacts(canRead ? careCircleId : undefined);
  const factsFetchedAt = useExtractedFactsFetchedAt(
    canRead ? careCircleId : undefined,
  );
  const isFactsLoaded = useIsExtractedFactsLoaded(
    canRead ? careCircleId : undefined,
  );
  const questionsBeat = flow.beats.find(
    (beat): beat is Extract<OnboardingV4Beat, { kind: 'questions' }> =>
      beat.kind === 'questions',
  );
  const questions = useMemo(
    () => triageQuestions(questionsBeat?.questionIDs ?? []),
    [questionsBeat?.questionIDs],
  );
  // The age is read in every flow, as an under 18 is stopped wherever it is known.
  const predictedIDs = useMemo(
    () => Array.from(new Set([...questions.map((q) => q.id), AGE_BRACKET_ID])),
    [questions],
  );
  // What the care profile already holds, from anywhere it was answered, under
  // what this conversation has answered and the profile is still to save.
  const careProfileId = useSelector(getCurrentCareCircle)?.careProfile?.id;
  const { data: profileAnswers } = useGetCareProfileAnswersQuery(
    careProfileId ?? '',
    { skip: !careProfileId },
  );
  const ageAgain = progress?.ageAgain;
  const ageSaidIDs =
    ageAgain?.kind === 'said' ? ageAgain.messageIDs : undefined;
  const ageSaidFrom = ageAgain?.kind === 'said' ? ageAgain.from : undefined;
  // Said again in their own words, an age read from it replaces the one tapped before.
  const isAgeHeard = useMemo(
    () =>
      !!ageSaidIDs?.length &&
      isAnsweredFrom(
        facts,
        apiMessages.filter((m) => ageSaidIDs.includes(m.id)),
        AGE_BRACKET_ID,
      ),
    [ageSaidIDs, apiMessages, facts],
  );
  // Kept as heard once it is, so the tap before does not come back should the fact go.
  useEffect(() => {
    if (isAgeHeard) saveProgress(ageHeard());
  }, [isAgeHeard, saveProgress]);
  const isAgeReplaced = ageAgain?.kind === 'heard' || isAgeHeard;
  const answers = useMemo(() => {
    const known = {
      ...answersByQuestion(profileAnswers ?? []),
      ...answersFrom(apiMessages),
    };
    if (isAgeReplaced) delete known[AGE_BRACKET_ID];
    return known;
  }, [apiMessages, isAgeReplaced, profileAnswers]);
  const saidInOwnWords = progress?.saidInOwnWords;
  const knowledge = useMemo<Knowledge>(
    () => ({
      answers,
      predicted: predictedAnswers(facts, predictedIDs),
      saidInOwnWords: saidInOwnWords ?? [],
    }),
    [answers, facts, predictedIDs, saidInOwnWords],
  );

  const [typedName, setTypedName] = useState<string>();
  const heardName = useMemo(() => namePartsFromFacts(facts), [facts]);
  const careRecipientName =
    typedName ??
    (heardName && formatFullName(heardName.firstName, heardName.lastName));
  const isWhoAsked = flow.beats.some((b) => b.kind === 'who');
  // Not asked who it is for, it follows what they said, so a correction moves it.
  const who = isWhoAsked ? progress?.who : whoSaid(facts);
  // With no who asked, it is someone they look after unless they say otherwise.
  const person = useMemo<Person>(
    () => ({
      isSelf: who === 'myself',
      name:
        who === 'myself' || who === 'exploring'
          ? undefined
          : firstNameOf(careRecipientName),
    }),
    [careRecipientName, who],
  );
  const { readingStyle } = flow;
  const heardRound = progress?.heardRound ?? 0;

  const beat = progress ? currentBeat(flow, progress) : null;
  const isAsked = (id: string) => !!progress?.asked.includes(id);

  // What cuts in ahead of the beat: the under 18 stop, or the age asked again.
  const interjection =
    beat && progress
      ? interjectionFor({ beat, knowledge, progress, person })
      : undefined;
  const question = progress
    ? questionFor({ interjection, beat, questions, knowledge, progress })
    : null;
  const isGathering = beat?.kind === 'gather' && isAsked('gather');
  // Open from when it is first asked about, as they may answer by saying it.
  const isNeedOpen = (beat?.kind === 'need' && isAsked('need')) || isGathering;

  const { isThinking, speak } = useChatSystemSender(
    persistMessage,
    thinkDelayMs,
  );

  /** Their reply, by its message id. */
  const reply = (text: string) => {
    const said = textMessage('user', text);
    persistMessage(said);
    return said.id;
  };

  // The menu still being asked, whose rows answer the step.
  const menuPicks = progress?.menuPicks;
  const menuID = useMemo(
    () => openMenuID(messages, menuPicks ?? {}),
    [menuPicks, messages],
  );
  /** Their answer, shown as the pick on the menu it answered rather than as a reply. */
  const pickOnMenu = (option: ConversationalFormOption, replyID: string) => {
    if (menuID) saveProgress(menuPicked(menuID, option.id, replyID));
  };

  const isRead = useCallback(
    (id: string) => {
      const msg = apiMessages.find((m) => m.id === id);
      return !!msg && isMessageRead(msg, processed);
    },
    [apiMessages, processed],
  );
  /** Sent with what they say so it is read in context. */
  const context = askFor({
    beat,
    flow,
    question,
    person,
    progress: progress ?? undefined,
    interjection,
  });

  // What they send waits for the next for a quiet window, so an answer split
  // across messages is read as one; a release acts on the beat as it is then.
  const typingWait = useOnboardingTypingWait().value;
  const hold = useSendHold({
    send: sendMessage,
    windowMs: typingWait.windowMs,
    capMs: typingWait.capMs,
    onRelease: (messageIDs, said) =>
      tookOwnWords(messageIDs, said.length ? said.join(' ') : undefined),
  });
  const { onDraftChange: draftChanged } = hold;
  useEffect(() => draftChanged(liveText), [draftChanged, liveText]);
  const sender = useChatSender({
    careCircleId,
    source: CAPTURE_SOURCE,
    documentTag: DocumentTagID.Onboarding,
    context,
    canRead,
    persistMessage,
    updateMessage,
    sendMessage: hold.sendMessage,
  });
  const need = useNeedChecklist({
    careCircleId,
    items: NEED_ITEMS,
    facts,
    person,
    isGathering,
    isOpen: isNeedOpen,
    liveText,
    context,
    source: CAPTURE_SOURCE,
    isEnabled: isCoverageFlagOn && canRead,
    isRead,
  });

  const readingIDs = progress?.readingIDs;
  const readingFrom = progress?.readingFrom;
  const isAllRead = !!readingIDs?.length && readingIDs.every(isRead);
  const statusOf = (id: string) => apiMessages.find((m) => m.id === id)?.status;
  // Sent, or failed to send: either way nothing more is coming to be read.
  const isAllSent =
    !!readingIDs?.length &&
    readingIDs.every((id) => {
      const status = statusOf(id);
      return status === 'sent' || status === 'error';
    });
  const isSummaryFirst = flow.talkWaitsFor === 'summary';

  // Asked for as soon as what they said is in, when the summary comes first.
  const intakeSummary = useIntakeSummary({
    careCircleId,
    isEnabled: canRead,
    isActive:
      canRead &&
      (!!progress?.done.includes('talk') || (isSummaryFirst && isAllSent)),
    submissionCount: sentCount,
  });

  const isWaitOver = useHasElapsed(
    readingFrom ? Date.parse(readingFrom) : undefined,
    flow.readingWaitMs,
  );
  const isReadingFacts = !!readingFrom && !isAllRead && !isWaitOver;
  const isTalkRead = isSummaryFirst
    ? intakeSummary.status !== 'reading'
    : isAllRead;
  // An age said again is read before the under 18 stop is judged again,
  // on its own clock, as the talk may still be to come.
  const isAgeWaitOver = useHasElapsed(
    ageSaidFrom ? Date.parse(ageSaidFrom) : undefined,
    flow.readingWaitMs,
  );
  // The message is marked read before the facts are fetched again, so what
  // it said is in only once they have been, or once an age shows from it.
  const isAgeSaidRead = !!ageSaidIDs?.length && ageSaidIDs.every(isRead);
  const isFactsRefetched = useHasChangedSince(
    factsFetchedAt,
    isAgeSaidRead ? ageSaidFrom : undefined,
  );
  // Nothing of it sent, there are no facts to wait for.
  const isAgeSaidLost =
    !!ageSaidIDs?.length && ageSaidIDs.every((id) => statusOf(id) === 'error');
  const isAgeBeingRead =
    canRead &&
    !!ageSaidIDs?.length &&
    !isAgeSaidLost &&
    !(isAgeSaidRead && (isFactsRefetched || isAgeHeard)) &&
    !isAgeWaitOver;
  // The talk waits on its reading; questions after a summary wait on the facts.
  const isReading =
    (beat?.kind === 'talk' && !!readingFrom && !isTalkRead && !isWaitOver) ||
    (beat?.kind === 'questions' &&
      isReadingFacts &&
      !isAsked(QUESTIONS_INTRO)) ||
    isAgeBeingRead;

  const reading = useMemo<ChatCardItem[]>(
    () =>
      readingItems({
        hasVoiceNote: includesVoiceNote(apiMessages, readingIDs),
        isAllRead,
        questions,
        person,
        predicted: knowledge.predicted,
      }),
    [
      apiMessages,
      isAllRead,
      knowledge.predicted,
      person,
      questions,
      readingIDs,
    ],
  );
  const isSentRead = !!progress?.isSentRead;
  useEffect(() => {
    if (isAllRead && !isSentRead) saveProgress(sentRead());
  }, [isAllRead, isSentRead, saveProgress]);

  const [summaryWait, setSummaryWait] = useState<{
    round: number;
    at: number;
  }>();
  const isSummaryWaitedFor = summaryWait?.round === heardRound;
  const isSummaryLate = useHasElapsed(
    isSummaryWaitedFor ? summaryWait.at : undefined,
    SUMMARY_WAIT_MS,
  );
  // After "Not quite", the summary read back must be a newer one than before.
  const isSummaryNew =
    heardRound === 0 ||
    (!!intakeSummary.unconfirmed &&
      intakeSummary.unconfirmed.id !== progress?.heardSummaryID);
  const isSummaryReady = intakeSummary.status !== 'reading' && isSummaryNew;

  /** Applies a move on from a beat. */
  const perform = (then: Transition) => {
    switch (then.kind) {
      case 'asked':
        markAsked(...then.ids);
        // The stop is tracked as it is said, once for each time.
        if (progress && then.ids.includes(under18Key(progress))) {
          track(TrackingEvent.OnboardingV4StoppedUnder18, {
            flowID: flow.id,
            beatID: beat?.id,
          });
        }
        return;
      case 'finished':
        finishBeat(then.beatID, then.changes);
        return;
      case 'changed':
        saveProgress(then.change);
        return;
      case 'staged':
        setStage(then.stage);
        return;
      default:
        return;
    }
  };

  // Keni's next move is worked out from where they are after each render,
  // and taken once: her lines mark themselves as said when they land.
  useEffect(() => {
    if (!progress || !sessionId || isThinking) return;
    if (progress.stage !== 'conversation') return;
    const action = nextAction({
      flow,
      progress,
      beat,
      person,
      questions,
      question,
      knowledge,
      canRead,
      isVoiceOn: isVoiceFlagOn && canRead,
      isReading,
      reading,
      needLabels: need.items.map((item) => item.label),
      isPhoneKnown: isPhoneKnown(facts),
      summary: {
        status: intakeSummary.status,
        lines: intakeSummary.lines,
        unconfirmedID: intakeSummary.unconfirmed?.id,
        isReady: isSummaryReady,
        isLate: isSummaryLate,
        isWaitedFor: isSummaryWaitedFor,
      },
    });
    if (!action) return;
    switch (action.kind) {
      case 'say':
        speak(action.lines, action.then && (() => perform(action.then!)));
        return;
      case 'move':
        perform(action.then);
        return;
      case 'waitForSummary':
        setSummaryWait({ round: heardRound, at: Date.now() });
        return;
      default:
        return;
    }
  });

  const failed = (description: string) =>
    dispatch(
      newToast({
        title: 'Something went wrong',
        description,
        variant: 'error',
      }),
    );

  const consent = async (option: ConversationalFormOption) => {
    const isGranted = option.id === CONSENT_AGREE;
    const res = await updateAccountConsent({
      consents: { [AI_CONSENT_FORM_ID]: isGranted },
    });
    if ('error' in res) {
      failed('We could not save that answer. Please try again.');
      return;
    }
    if (!isGranted) {
      onDeclined();
      return;
    }
    // Asked in the chat, agreeing is their reply to Keni.
    if (flow.consentStyle === 'chat') pickOnMenu(option, reply(option.label));
    finishBeat('ai-consent', { isAiConsentGiven: true });
  };

  /** Saves who the care is for as the care circle's type. */
  const saveCareRecipientType = useCallback(
    async (chosen: Who) => {
      if (!careCircleId) return;
      const res = await handleTriageResponses({
        careCircleID: careCircleId,
        responses: [
          {
            questionID: TRIAGE_QUESTION_ID.careRecipientType,
            response: TRIAGE_CARE_RECIPIENT_TYPE[chosen],
          },
        ],
      });
      // The flow carries on; a care circle's type can be set again later.
      if ('error' in res) Sentry.captureException(res.error);
    },
    [careCircleId, handleTriageResponses],
  );

  // Saved once per care circle and answer, however often it is read again.
  const savedRecipientType = useRef<string>(undefined);
  useEffect(() => {
    if (isWhoAsked || !who || !careCircleId) return;
    const saved = `${careCircleId}:${who}`;
    if (savedRecipientType.current === saved) return;
    savedRecipientType.current = saved;
    saveCareRecipientType(who);
  }, [careCircleId, isWhoAsked, saveCareRecipientType, who]);

  const chooseWho = (option: ConversationalFormOption) => {
    const chosen = option.id as Who;
    pickOnMenu(option, reply(option.label));
    finishBeat('who', { who: chosen });
    saveCareRecipientType(chosen);
  };

  /** Their answers to a question, by the message's id. */
  const sendAnswers = (q: TriageQuestion, optionIDs: string[]) => {
    const answers = message('user', {
      kind: 'answers',
      text: optionLabels(q, optionIDs),
      answers: [{ questionId: q.id, optionIds: optionIDs }],
    });
    persistMessage(answers);
    // An age tapped, here or on the details screen, counts over one said before.
    if (q.id === AGE_BRACKET_ID && ageAgain) saveProgress(ageTapped());
    return answers.id;
  };

  /** "Change the age" from the under 18 stop: it is asked again. */
  const changeAge = (option: ConversationalFormOption) => {
    pickOnMenu(option, reply(option.label));
    saveProgress(ageChangeAsked());
    track(TrackingEvent.OnboardingV4AgeChanged, { flowID: flow.id });
  };

  const answerQuestion = (option: ConversationalFormOption) => {
    if (!question) return;
    pickOnMenu(option, sendAnswers(question, [option.id]));
    track(TrackingEvent.OnboardingV4QuestionAnswered, {
      flowID: flow.id,
      questionID: question.id,
      how: 'tapped',
    });
  };

  // Held at the top while they talk, so each tick stays in view; they can put it back.
  const isPromptsPinnable =
    flow.talkOpening === 'open' && beat?.kind === 'talk';
  // Saying more before the summary is confirmed, what they say ticks the
  // prompts too, and brings them back while one is still unticked. Answering
  // what they need help with is not saying more.
  const isSayingMore =
    flow.talkOpening === 'open' &&
    beat?.kind === 'heard' &&
    isAsked(heardKey(heardRound)) &&
    !isAsked(helpWithKey(heardRound)) &&
    isComposing;
  const prompts = usePinnedPrompts({
    careCircleId,
    prompts: copy.OPENING.prompts,
    apiMessages,
    messages,
    liveText,
    isPinnable: isPromptsPinnable,
    isListening: isPromptsPinnable || isSayingMore,
    isEnabled: isCoverageFlagOn && canRead && flow.talkOpening === 'open',
  });
  const isPromptsBack = isSayingMore && prompts.isAnyLeft;

  // Coverage says nothing until it is checked, so it is only left out where
  // something else has been ticked and it has not.
  const isHelpWithLeftOut =
    beat?.kind === 'heard' &&
    !!beat.asksHelpWith &&
    !!progress &&
    !isHelpWithAsked(progress) &&
    Object.values(prompts.states).some((state) => state !== 'waiting') &&
    prompts.states[copy.HELP_WITH_PROMPT] !== 'done';
  /** Reading what they need help with, before the summary comes back. */
  const isReadingHelpWith =
    beat?.kind === 'heard' &&
    !!progress &&
    heardRound > 0 &&
    isHelpWithAskedIn(progress, heardRound - 1);

  /** The card reading back what we heard this round, the one that can be corrected. */
  const heardCardID = isAsked(heardKey(heardRound))
    ? lastCardID(apiMessages, 'heard')
    : undefined;

  /** Their correction of the summary is read too, so the facts follow it. */
  const readSummaryEdits = (cardID: string | undefined) => {
    const edited = cardID && progress?.summaryEdits[cardID];
    if (!edited) return;
    sender.captureText(edited.join('\n\n'), cardID, copy.HEARD.editedContext);
  };

  const answerHeard = async (option: ConversationalFormOption) => {
    reply(option.label);
    if (option.id === HEARD_NOT_QUITE) {
      markAsked(correctingKey(heardRound));
      speak([say(BEAT_STEPS.correcting.ask)]);
      track(TrackingEvent.OnboardingV4SummaryCorrected, { flowID: flow.id });
      return;
    }
    // What they need help with left out, it is asked before the summary is checked again.
    if (isHelpWithLeftOut) {
      markAsked(helpWithKey(heardRound));
      speak([say(copy.HELP_WITH.ask)]);
    } else {
      finishBeat('heard');
    }
    track(TrackingEvent.OnboardingV4SummaryConfirmed, { flowID: flow.id });
    const cardID = heardCardID;
    try {
      await intakeSummary.onConfirm();
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      // The summary is kept whether or not it is marked confirmed.
      readSummaryEdits(cardID);
    }
  };

  /** Their correction of a card's lines, shown in its place. */
  const editSummary = (cardID: string, lines: string[]) =>
    saveProgress(summaryEdited(cardID, lines));

  const answerHelp = (option: ConversationalFormOption) => {
    reply(option.label);
    finishBeat('help', { wantsHelp: option.id });
  };

  // Later skips sending it now.
  const answerNeed = (option: ConversationalFormOption) => {
    reply(option.label);
    if (option.id === YES) {
      finishBeat('need');
      return;
    }
    saveProgress(needSkipped());
  };

  const answerGather = (option: ConversationalFormOption) => {
    reply(option.label);
    finishBeat('gather');
  };

  // Left for later, the phone number moves on without one.
  const answerPhone = (option: ConversationalFormOption) => {
    reply(option.label);
    finishBeat('phone');
  };

  /** Their phone number: read where it is a UK one, said back where it is not. */
  const givePhone = (typed: string) => {
    const problem = ukPhoneProblem(typed);
    if (problem) {
      reply(typed);
      speak([say(copy.PHONE.problem[problem])]);
      return;
    }
    sender.sendText(typed);
    hold.flush();
    finishBeat('phone');
  };

  // Where the details are not checked on a screen, it finishes from here.
  const chooseStart = (option: ConversationalFormOption) => {
    pickOnMenu(option, reply(option.label));
    finishBeat('start', { start: option.id });
    if (!flow.checksDetails) return;
    speak([say(copy.START.thanks)], () => setStage('confirm'));
  };

  const answerWithOption = (option: ConversationalFormOption) => {
    if (!beat || isThinking) return;
    // A tap says they are done with what they typed before it; if that moved the beat on, it answered.
    if (hold.flush()) return;
    // The stop's one row, even once the stop has lifted beneath it.
    if (option.id === CHANGE_AGE) {
      changeAge(option);
      return;
    }
    if (interjection?.kind === 'age-again') {
      answerQuestion(option);
      return;
    }
    switch (beat.kind) {
      case 'welcome':
        finishBeat('welcome');
        return;
      case 'consent':
        consent(option);
        return;
      case 'who':
        chooseWho(option);
        return;
      case 'questions':
        answerQuestion(option);
        return;
      case 'heard':
        answerHeard(option);
        return;
      case 'help':
        answerHelp(option);
        return;
      case 'need':
        answerNeed(option);
        return;
      case 'gather':
        answerGather(option);
        return;
      case 'phone':
        answerPhone(option);
        return;
      case 'start':
        chooseStart(option);
        return;
      default:
        return;
    }
  };

  /** A question answered in their own words, in the questions or the age asked again. */
  const answerInOwnWords = (
    q: TriageQuestion,
    messageIDs: string[],
  ): boolean => {
    const isAgeAgain = interjection?.kind === 'age-again';
    // Unread, an age said again cannot be taken: it stays asked for a tap.
    if (isAgeAgain && !canRead) {
      speak([say(copy.UNDER_18.tapAge)]);
      return false;
    }
    saveProgress(answeredInOwnWords(q.id));
    if (isAgeAgain) saveProgress(ageSaid(messageIDs, new Date().toISOString()));
    track(TrackingEvent.OnboardingV4QuestionAnswered, {
      flowID: flow.id,
      questionID: q.id,
      how: 'own words',
    });
    speak([say(copy.QUESTIONS.saidInOwnWords)]);
    return true;
  };

  /** Moves the current beat on from something sent in their own words, once it goes to be read, with those words where there are any; true when it moved the beat on. */
  const tookOwnWords = (messageIDs: string[], said?: string): boolean => {
    if (question) return answerInOwnWords(question, messageIDs);
    // Stopped for an under 18, what they say is read but moves nothing on.
    if (interjection) return false;
    // Sent while what they said is still being read, it is read with it.
    if (beat?.kind === 'talk' && !!progress?.readingFrom && isReading) {
      saveProgress(readingJoined(messageIDs, new Date().toISOString()));
      return false;
    }
    if (beat?.kind === 'talk' && !progress?.readingFrom) {
      if (!canRead) {
        finishBeat('talk');
        return true;
      }
      // Too little to go on: they are asked for more before it is read.
      if (said !== undefined && words(said).length < MIN_TALK_WORDS) {
        speak([say(copy.STATES.tooShort)]);
        return false;
      }
      saveProgress(readingStarted(messageIDs, new Date().toISOString()));
      return true;
    }
    // Said in place of tapping, or after "Not quite": either way, a
    // correction, read back again once it is read with everything else.
    if (beat?.kind === 'heard' && isAsked(heardKey(heardRound))) {
      if (!canRead) {
        speak([say(copy.HEARD.thanks)], () => finishBeat('heard'));
        return true;
      }
      saveProgress(heardAgain(messageIDs, new Date().toISOString()));
      return true;
    }
    // Saying yes rather than tapping it moves on the same way.
    if (beat?.kind === 'help' && isAsked('help')) {
      finishBeat('help', { wantsHelp: 'said' });
      return true;
    }
    if (beat?.kind === 'need' && isAsked('need')) {
      finishBeat('need');
      return true;
    }
    if (isGathering) {
      saveProgress(gathered(messageIDs));
    }
    return false;
  };

  /** What it ticked stays ticked until what was sent is read, held or not. */
  const holdTicks = (messageIDs: string[]) => need.holdDraft(messageIDs);

  const sendText = (text: string) => {
    if (!interjection && beat?.kind === 'phone' && isAsked('phone')) {
      givePhone(text);
      return;
    }
    holdTicks([sender.sendText(text)]);
  };

  /**
   * Read from its transcript when there is one worth reading, else from the
   * audio; kept from the recording the API holds when there is one.
   */
  const sendVoiceNote = (
    file: File,
    durationSeconds: number,
    levels: readonly number[] = [],
    transcript: string | null = null,
    recordingId: string | null = null,
  ) => {
    const isTranscribed = !!transcript && canRead;
    track(TrackingEvent.AudioRecordingCompleted, {
      durationSeconds,
      flowID: flow.id,
      isTranscribed,
    });
    // A voice note is whole: it goes at once, with anything typed before it.
    holdTicks([
      sender.sendVoiceNote(
        file,
        durationSeconds,
        levels,
        isTranscribed ? transcript : null,
        recordingId,
      ),
    ]);
  };

  const attachFiles = (files: File[]) => {
    if (!files.length) return;
    holdTicks(sender.sendFiles(files));
  };

  // Photos make a message whole: nothing waits.
  const sendWithPhotos = (text: string, photos: File[]) => {
    holdTicks([
      ...(text ? [sender.sendText(text)] : []),
      ...sender.sendFiles(photos),
    ]);
  };

  const nextStep =
    beat && progress && !isThinking
      ? stepFor({
          flow,
          beat,
          progress,
          person,
          question,
          knowledge,
          isVoiceOn: isVoiceFlagOn && canRead,
          isFileOn: isFileFlagOn && canRead,
        })
      : undefined;

  const isWritingUp =
    beat?.kind === 'heard' &&
    canRead &&
    !isAsked(heardKey(heardRound)) &&
    !isSummaryReady &&
    !isSummaryLate;

  const needCardID = lastCardID(apiMessages, 'need');
  const summaryEdits = progress?.summaryEdits;

  // What they sent is shown as being read until it is.
  const sentForReading = useMemo(
    () => [
      ...(readingIDs ?? []),
      ...(ageSaidIDs ?? []),
      ...(progress?.gatheredIDs ?? []),
    ],
    [ageSaidIDs, progress?.gatheredIDs, readingIDs],
  );

  // Replies shown as the pick on their menu are left out.
  const pickedReplyIDs = progress?.pickedReplyIDs;
  const hiddenIDs = useMemo(() => pickedReplyIDs ?? [], [pickedReplyIDs]);
  const shown = useMemo<ChatMessage[]>(
    () =>
      shownMessages(messages, {
        hiddenIDs,
        sentForReading,
        isRead,
        promptStates: prompts.states,
        isPromptsPinnable,
        neededItems: need.items,
        readingItems: reading,
        isSentRead,
        summaryEdits,
        heardCardID,
        menuPicks,
        openMenuID: menuID,
      }),
    [
      heardCardID,
      hiddenIDs,
      menuID,
      menuPicks,
      isPromptsPinnable,
      isRead,
      isSentRead,
      messages,
      need.items,
      prompts.states,
      reading,
      sentForReading,
      summaryEdits,
    ],
  );
  // On a session picked up again, the needs and prompts ticked before are
  // read back after it loads, and are not new.
  useOnboardingHaptics({
    messages: shown,
    isReady: !!progress,
    isTicksSettled: isFactsLoaded && prompts.isCaughtUp,
  });

  // A checklist being worked on is held at the top of the chat once scrolled to.
  const heldCardID = isGathering
    ? needCardID
    : prompts.isPinned || isPromptsBack
      ? prompts.cardID
      : undefined;

  // Between one question and the next, the last one's bar stays where it is,
  // disabled, rather than dropping away and coming back with the next.
  const lastChatStep = useRef<OnboardingV4Step>(undefined);
  useEffect(() => {
    if (nextStep?.presentation === 'chat') lastChatStep.current = nextStep;
  }, [nextStep]);
  const isBetweenSteps =
    !nextStep &&
    !!beat &&
    !isReading &&
    !isWritingUp &&
    progress?.stage === 'conversation' &&
    !!lastChatStep.current;
  const currentStep = isBetweenSteps ? lastChatStep.current : nextStep;

  // Coming back to a conversation waiting on them, Keni says so, once a visit.
  const [isWelcomeChecked, setIsWelcomeChecked] = useState(false);
  useEffect(() => {
    if (isWelcomeChecked || !progress || !sessionId || isThinking) return;
    setIsWelcomeChecked(true);
    const isWaiting =
      progress.stage === 'conversation' &&
      nextStep?.presentation === 'chat' &&
      !isReading;
    if (apiMessages.length && isWaiting) speak([say(copy.STATES.resumed)]);
  }, [
    apiMessages.length,
    isReading,
    isThinking,
    isWelcomeChecked,
    nextStep?.presentation,
    progress,
    sessionId,
    speak,
  ]);

  // The microphone refused, Keni says how to let it in, once a visit.
  const [isMicBlockedSaid, setIsMicBlockedSaid] = useState(false);
  useEffect(() => {
    if (!isMicBlocked || isMicBlockedSaid || !progress || isThinking) return;
    setIsMicBlockedSaid(true);
    speak([say(copy.STATES.micBlocked)]);
  }, [isMicBlocked, isMicBlockedSaid, isThinking, progress, speak]);

  const confirmation = useOnboardingV4Confirm({
    careCircleId,
    questions,
    knowledge,
    person,
    who,
    name: {
      value: careRecipientName,
      isTyped: !!typedName,
      setTyped: setTypedName,
    },
    sendAnswers,
    onConfirmed: () => {
      setStage('finished');
      completeSession();
      track(TrackingEvent.OnboardingV4Completed, {
        flowID: flow.id,
        careCircleID: careCircleId,
        start: progress?.start,
      });
      dispatch(invalidateNotificationTags());
      dispatch(clearIsNewKindeAccount());
      onFinished(progress?.start);
    },
  });

  // Not checked on a screen, it finishes once the conversation does; the
  // details screen shows only if that fails, to try again from.
  const stage = progress?.stage ?? 'conversation';
  const [isFinishTried, setIsFinishTried] = useState(false);
  useEffect(() => {
    if (stage !== 'confirm' || flow.checksDetails || isFinishTried) return;
    setIsFinishTried(true);
    confirmation.confirm();
  });
  const isFinishingInChat =
    stage === 'confirm' &&
    !flow.checksDetails &&
    (!isFinishTried || confirmation.isFinishing);

  return {
    flow,
    isSessionReady: !!progress,
    stage,
    isFinishingInChat,
    beat,
    messages: shown,
    heldCardID,
    togglePinned: prompts.togglePinned,
    readingStyle,
    currentStep,
    choiceStyle: choiceStyleFor(beat, flow, currentStep),
    isThinking: isThinking || isBetweenSteps,
    isReading,
    isWritingUp,
    readingLines: isReadingHelpWith ? copy.HELP_WITH.readingLines : undefined,
    readingItems: reading,
    section: sectionFor(beat, person, flow),
    confirmCategories: confirmation.categories,
    isFinishing: confirmation.isFinishing,
    answerWithOption,
    sendText,
    sendVoiceNote,
    attachFiles,
    sendWithPhotos,
    onDraftBlur: hold.onDraftBlur,
    resendMessage,
    saveFact: confirmation.saveFact,
    confirm: confirmation.confirm,
    editSummary,
    openMenuID: currentStep?.options?.length ? menuID : undefined,
  };
};
